import ApiEventTypes from "api/ApiEventTypes"
import SelectV2, { Option } from "components/EvtKit/select/SelectV2"
import { useQueryState } from "context/queryState"
import { List } from "hooks/useHandleObjList"
import { memo, useCallback, useEffect, useRef, useState } from "react"
import { EVENT_TYPE_OPTIONS } from "reducers/purchasePlan/purchase-plan-actions"
import { useAppSelector } from "reducers/store"
import { useDebouncedCallback } from "use-debounce"
import useDeepCompareEffect from "use-deep-compare-effect"
import { noop } from "utils/noop"

interface EventTypesSelectorPros {
  onSelectorChange: (e: Option | null) => void
  value?: string
  setPrices?: (pricesByEventType: Record<string, string>) => void
  disabled?: boolean
  pricingVariablesList?: List<App.ISimplePricingVariable>
}

const EventTypeSelector = (props: EventTypesSelectorPros) => {
  const { onSelectorChange, value = "", setPrices, disabled = false, pricingVariablesList } = props

  const [options, setOptions] = useState<App.SelectOptions[]>()

  const { productId } = useAppSelector((state) => state.purchasePlan.identifiers)
  const { product: initialProduct } = useAppSelector((state) => state.purchasePlan.purchasePlanInfo.data)

  useQueryState(
    [EVENT_TYPE_OPTIONS, productId],
    async () => await new ApiEventTypes().getEventTypesOptions(productId),
    EVENT_TYPE_OPTIONS,
    { refetchOnMount: "always" }
  )

  const {
    eventTypeOptions: { data, loading }
  } = useAppSelector((state) => state.purchasePlan)

  const [forcedLoading, setLoading] = useState(false)

  const forceLoadingEnd = useDebouncedCallback(() => {
    setLoading(false)
  }, 1)

  const initialSelectorValue = useRef(value)

  const handleSelectorValueReset = useCallback(() => {
    if (initialProduct.id === productId) {
      onSelectorChange({ label: "", value: initialSelectorValue.current })

      return
    }

    onSelectorChange(null)
  }, [initialProduct.id, initialProduct.name, productId])

  useEffect(() => {
    handleSelectorValueReset()

    //force loading state changes to mount selector again when product is updated
    setLoading(true)
    forceLoadingEnd()
  }, [productId])

  const handleFilterOptions = useCallback(() => {
    if (!pricingVariablesList) {
      return
    }

    const definedEventTypes = pricingVariablesList.filter((v) => v.eventType !== "").map((v) => v.eventType)

    const eventTypesSet = new Set(definedEventTypes)

    const filteredOptions: App.SelectOptions[] = []

    data.options.forEach((option) => {
      if (!eventTypesSet.has(option.value) || value === option.value) filteredOptions.push(option)
    })

    if (filteredOptions.length > 0) setOptions(filteredOptions)
  }, [data.options, pricingVariablesList, value])

  useDeepCompareEffect(() => {
    handleFilterOptions()

    // force loading state changes to mount selector again when pricingVariablesList is updated
    if (!disabled) {
      setLoading(true)
      forceLoadingEnd()
    }
  }, [pricingVariablesList, handleFilterOptions, disabled])

  useEffect(() => {
    setPrices?.(data.prices)
  }, [data.prices, productId])

  return (
    <>
      {/* Transition component that serves as a placeholder for forced change of selector options and loading */}
      {(loading || forcedLoading) && (
        <SelectV2
          className="fullWidth"
          id={`event-type-${productId}`}
          label="Tipo de Evento *"
          onChange={noop}
          placeholder="Selecione"
          shrink
          value={value}
          options={options ?? data.options}
          disabled={disabled}
        />
      )}

      {!(loading || forcedLoading) && (
        <SelectV2
          className="fullWidth"
          id={`event-type-${productId}`}
          label="Tipo de Evento *"
          onChange={onSelectorChange}
          placeholder="Selecione"
          shrink
          value={value}
          options={options ?? data.options}
          disabled={disabled}
        />
      )}
    </>
  )
}

export default memo(EventTypeSelector)
